/**
 * EmptyState — canonical "no data" panel for sysadmin pages.
 *
 * Per SYSADMIN-PLATFORM-SPEC §11.8:
 *   - Centered, py-12
 *   - Muted icon at 32px (h-8 w-8)
 *   - title (one line)
 *   - body (short paragraph, optional)
 *   - optional primary action
 *
 * Used on every sysadmin list/table page when the query returns 0 rows
 * and on landing pages before the user picks a sub-tab. Replaces the
 * scattered "py-8 text-center text-muted-foreground" inline blocks
 * across `audit.stream.tsx`, `catalog.*`, `tenants/index.tsx`, etc.
 *
 * @example
 * <EmptyState
 *   icon={Inbox}
 *   title="No audit events"
 *   body="No platform events match the current filters."
 *   action={<Button>Clear filters</Button>}
 * />
 */

import type { ComponentType, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface EmptyStateProps {
  /** Icon component (lucide-react or custom) — rendered at 32px */
  icon?: LucideIcon | ComponentType<{ className?: string }>;
  /** Headline. Single line. */
  title: string;
  /** Optional body — single paragraph, short. */
  body?: ReactNode;
  /** Optional primary action (button / link). */
  action?: ReactNode;
  /** Tighten vertical padding (py-8 instead of py-12) for embedded use. */
  compact?: boolean;
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  body,
  action,
  compact = false,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'py-8 px-4' : 'py-12 px-6',
        className,
      )}
    >
      {Icon ? (
        <Icon className="h-8 w-8 text-muted-foreground mb-3" aria-hidden />
      ) : null}
      <div className="text-sm font-semibold">{title}</div>
      {body ? (
        <div className="mt-1.5 max-w-md text-xs text-muted-foreground leading-relaxed">
          {body}
        </div>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}
